/**
 * GameEngine - Core game logic and rules
 * Implements win detection, move validation, and game state transitions
 * Based on ADR-003: Game State Management Architecture
 */

import type { CellValue, Player, GameState, GameStatus } from '@/types';
import { GAME_CONSTANTS } from '@/types';

/**
 * Creates an empty game board
 */
export function createEmptyBoard(): CellValue[][] {
  return Array.from({ length: GAME_CONSTANTS.BOARD_SIZE }, () =>
    Array.from({ length: GAME_CONSTANTS.BOARD_SIZE }, () => null)
  );
}

/**
 * Creates initial game state
 */
export function createInitialGameState(): GameState {
  return {
    board: createEmptyBoard(),
    currentPlayer: 'X',
    gameStatus: 'playing',
    winner: null,
    winningCells: null,
  };
}

/**
 * Validates if a move is legal
 */
export function isValidMove(
  board: CellValue[][],
  row: number,
  col: number,
  gameStatus: GameStatus
): boolean {
  // Check game is still playing
  if (gameStatus !== 'playing') {
    return false;
  }

  // Check bounds
  if (row < 0 || row >= GAME_CONSTANTS.BOARD_SIZE || 
      col < 0 || col >= GAME_CONSTANTS.BOARD_SIZE) {
    return false;
  }

  // Check cell is empty
  const cell = board[row];
  if (!cell) return false;
  return cell[col] === null;
}

/**
 * Checks for a winner on the board
 * Returns the winning player or null
 */
export function checkWinner(board: CellValue[][]): Player | null {
  const size = GAME_CONSTANTS.BOARD_SIZE;

  // Check rows
  for (let row = 0; row < size; row++) {
    const rowValues = board[row];
    if (!rowValues) continue;
    
    const firstCell = rowValues[0];
    if (firstCell && rowValues.every((cell) => cell === firstCell)) {
      return firstCell;
    }
  }

  // Check columns
  for (let col = 0; col < size; col++) {
    const firstCell = board[0]?.[col];
    if (firstCell && board.every((row) => row?.[col] === firstCell)) {
      return firstCell;
    }
  }

  // Check diagonal (top-left to bottom-right)
  const topLeft = board[0]?.[0];
  if (topLeft && board.every((row, idx) => row?.[idx] === topLeft)) {
    return topLeft;
  }

  // Check anti-diagonal (top-right to bottom-left)
  const topRight = board[0]?.[size - 1];
  if (topRight && board.every((row, idx) => row?.[size - 1 - idx] === topRight)) {
    return topRight;
  }

  return null;
}

/**
 * Gets the indices of winning cells (0-8 for 3x3 board)
 */
export function getWinningCells(board: CellValue[][]): number[] | null {
  const size = GAME_CONSTANTS.BOARD_SIZE;
  const winner = checkWinner(board);
  
  if (!winner) {
    return null;
  }

  // Check rows
  for (let row = 0; row < size; row++) {
    const rowValues = board[row];
    if (!rowValues) continue;
    
    const firstCell = rowValues[0];
    if (firstCell === winner && rowValues.every((cell) => cell === winner)) {
      return Array.from({ length: size }, (_, col) => row * size + col);
    }
  }

  // Check columns
  for (let col = 0; col < size; col++) {
    const firstCell = board[0]?.[col];
    if (firstCell === winner && board.every((row) => row?.[col] === winner)) {
      return Array.from({ length: size }, (_, row) => row * size + col);
    }
  }

  // Check diagonal (top-left to bottom-right)
  const topLeft = board[0]?.[0];
  if (topLeft === winner && board.every((row, idx) => row?.[idx] === winner)) {
    return Array.from({ length: size }, (_, idx) => idx * size + idx);
  }

  // Check anti-diagonal (top-right to bottom-left)
  const topRight = board[0]?.[size - 1];
  if (topRight === winner && board.every((row, idx) => row?.[size - 1 - idx] === winner)) {
    return Array.from({ length: size }, (_, idx) => idx * size + (size - 1 - idx));
  }

  return null;
}

/**
 * Checks if the board is full (all cells occupied)
 */
export function isBoardFull(board: CellValue[][]): boolean {
  return board.every((row) => row?.every((cell) => cell !== null) ?? false);
}

/**
 * Makes a move and returns the new game state
 * Uses immutable updates to prevent state corruption
 */
export function makeMove(state: GameState, row: number, col: number): GameState {
  // Validate move
  if (!isValidMove(state.board, row, col, state.gameStatus)) {
    return state; // Return unchanged state for invalid moves
  }

  // Create new board with the move
  const newBoard = state.board.map((boardRow, rowIndex) =>
    rowIndex === row
      ? boardRow.map((cell, colIndex) => (colIndex === col ? state.currentPlayer : cell))
      : [...boardRow]
  );

  // Check for winner
  const winner = checkWinner(newBoard);
  const isDraw = winner === null && isBoardFull(newBoard);

  // Determine new game status
  let newStatus: GameStatus = 'playing';
  if (winner) {
    newStatus = 'won';
  } else if (isDraw) {
    newStatus = 'draw';
  }

  // Return new state
  return {
    board: newBoard,
    currentPlayer: state.currentPlayer === 'X' ? 'O' : 'X',
    gameStatus: newStatus,
    winner: winner,
    winningCells: winner ? getWinningCells(newBoard) : null,
  };
}

/**
 * Resets the game to initial state
 */
export function resetGame(): GameState {
  return createInitialGameState();
}
